import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);

const bookingBase = window.location.host;

const Field = ({ label, testId, ...rest }) => (
  <div>
    <label className="mb-2 block text-xs uppercase tracking-widest text-gold font-mono">{label}</label>
    <Input
      data-testid={testId}
      className="h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-gold"
      {...rest}
    />
  </div>
);

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "", name: "", email: "", phone: "", handle: "", password: "",
    consent_terms: false, consent_privacy: false, consent_software_only: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [handleEdited, setHandleEdited] = useState(false);
  const [check, setCheck] = useState({ state: "idle", reason: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setCompanyName = (e) => {
    const company_name = e.target.value;
    setForm((f) => ({ ...f, company_name, handle: handleEdited ? f.handle : slugify(company_name) }));
  };

  const setHandle = (e) => {
    setHandleEdited(true);
    setForm((f) => ({ ...f, handle: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }));
  };

  useEffect(() => {
    const handle = form.handle.trim();
    if (!handle) return setCheck({ state: "idle", reason: "" });
    setCheck({ state: "checking", reason: "" });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/handle-available", { params: { handle } });
        setCheck({ state: data.available ? "free" : "taken", reason: data.reason });
      } catch {
        setCheck({ state: "idle", reason: "" });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.handle]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent_terms || !form.consent_privacy || !form.consent_software_only) {
      setError("Please accept all three agreements to continue.");
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      return;
    }
    if (form.handle.trim() && check.state === "taken") {
      setError(check.reason || "Please pick another booking link.");
      return;
    }
    setBusy(true);
    try {
      await register(form);
      navigate("/app");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const consents = [
    { key: "consent_terms", testId: "consent-terms", node: (<>I agree to the <Link to="/legal/terms" className="text-gold underline">Terms of Service</Link>.</>) },
    { key: "consent_privacy", testId: "consent-privacy", node: (<>I have read the <Link to="/legal/privacy" className="text-gold underline">Privacy Policy</Link>.</>) },
    { key: "consent_software_only", testId: "consent-software-only", node: <>I understand that the platform provides software services only and does not provide transportation services.</> },
  ];

  return (
    <div className="min-h-screen bg-ink-900 px-4 py-14 text-slate-200">
      <div className="mx-auto max-w-2xl rise">
        <Link to="/" className="flex items-center gap-2.5 font-display text-lg font-semibold">
          <img src="/limolinkx-logo.png" alt="" className="h-8 w-8 rounded bg-white/95 object-contain p-0.5" />
          <span className="gold-text">{PLATFORM_NAME}</span>
        </Link>
        <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
          Create your transportation company
        </h1>
        <p className="mt-3 text-sm text-slate-400">2 free trips every week — no payment required.</p>

        <form onSubmit={submit} className="mt-10 space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
          <Field label="Company name" testId="signup-company" value={form.company_name} onChange={setCompanyName} placeholder="ABC Executive Transportation" required />

          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">
              Your booking link <span className="text-slate-500">(optional)</span>
            </label>
            <div className="flex items-center rounded-md border border-ink-600 bg-ink-800 pl-3 focus-within:ring-1 focus-within:ring-gold">
              <span className="shrink-0 font-mono text-xs text-slate-500">
                <span className="hidden sm:inline">{bookingBase}</span>/ride/
              </span>
              <Input
                data-testid="signup-handle"
                value={form.handle}
                onChange={setHandle}
                placeholder="abc-executive"
                className="h-12 border-0 bg-transparent px-1.5 text-slate-100 placeholder:text-slate-600 focus-visible:ring-0"
              />
              <span className="w-9 shrink-0 pr-2 text-right">
                {check.state === "checking" && <Loader2 className="ml-auto h-4 w-4 animate-spin text-slate-500" />}
                {check.state === "free" && <Check data-testid="handle-ok" className="ml-auto h-4 w-4 text-emerald-400" />}
                {check.state === "taken" && <X data-testid="handle-bad" className="ml-auto h-4 w-4 text-red-400" />}
              </span>
            </div>
            <p data-testid="handle-hint" className={`mt-2 text-xs ${check.state === "taken" ? "text-red-400" : "text-slate-500"}`}>
              {check.state === "taken"
                ? check.reason
                : check.state === "free"
                ? "Available — this is where your clients book."
                : "Your own branded page for clients to book. You can change it later in Settings."}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Owner / administrator name" testId="signup-name" value={form.name} onChange={set("name")} placeholder="John Smith" required />
            <Field label="Phone" testId="signup-phone" value={form.phone} onChange={set("phone")} placeholder="713-555-1234" />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Email" testId="signup-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" required />
            <Field label="Password" testId="signup-password" type="password" value={form.password} onChange={set("password")} placeholder="Minimum 8 characters" required />
          </div>

          <div className="space-y-3.5 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
            {consents.map((c) => (
              <label key={c.key} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-300">
                <Checkbox
                  data-testid={c.testId}
                  checked={form[c.key]}
                  onCheckedChange={(v) => setForm({ ...form, [c.key]: Boolean(v) })}
                  className="mt-0.5 border-ink-600 data-[state=checked]:bg-gold data-[state=checked]:text-ink-900"
                />
                <span>{c.node}</span>
              </label>
            ))}
          </div>

          <details className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
            <summary data-testid="disclosure-toggle" className="cursor-pointer font-mono text-[11px] uppercase tracking-widest text-gold">
              Software provider disclosure
            </summary>
            <p className="mt-3 text-xs leading-relaxed text-slate-400">
              {PLATFORM_NAME} is a software technology provider. We do not provide transportation services,
              operate vehicles, employ chauffeurs on behalf of operators, sell transportation services or act as
              a transportation carrier. Transportation operators are solely responsible for their vehicles,
              chauffeurs, customers, reservations, pricing, permits, licenses, insurance, taxes, compliance and
              fulfilment of trips. Any transportation agreement is between the operator and its customer.
            </p>
          </details>

          {error && <p data-testid="signup-error" className="text-sm text-red-400">{error}</p>}

          <Button data-testid="signup-submit" type="submit" disabled={busy} className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create free account"}
          </Button>
          <p className="text-center text-sm text-slate-500">
            Already have an account? <Link to="/login" data-testid="to-login-link" className="text-gold">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
